import { Button, ButtonProps } from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { CALIBRATE_FONT_FAMILY_ACCENT } from "components/hoc/Theme";

/**
 * Shared dark CTA button. Tones: `solidMain` (modal submits), `solidLight`
 * (CTA banners / partner detail), `ghostOnDark` (on a dark teal surface).
 */
export type DarkCtaTone = "solidMain" | "solidLight" | "ghostOnDark";

const HOVER_SHADOW = "0 4px 8px rgba(0,0,0,0.25)";
const ACTIVE_SHADOW = "0 1px 2px rgba(0,0,0,0.25)";

const toneStyles: Record<DarkCtaTone, (theme: Theme) => object> = {
  solidMain: (theme) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.calibrate.buttonHoverSolid,
      boxShadow: HOVER_SHADOW,
    },
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      boxShadow: ACTIVE_SHADOW,
    },
    "&:disabled": {
      backgroundColor: alpha(theme.palette.primary.main, 0.5),
      color: "rgba(255,255,255,0.7)",
    },
  }),
  solidLight: (theme) => ({
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.calibrate.buttonHoverSolid,
      boxShadow: HOVER_SHADOW,
    },
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      boxShadow: ACTIVE_SHADOW,
    },
  }),
  ghostOnDark: (theme) => ({
    backgroundColor: "rgba(255,255,255,0.25)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#fff",
      color: theme.palette.calibrate.panelAccent,
      boxShadow: HOVER_SHADOW,
    },
    "&:active": {
      backgroundColor: theme.palette.calibrate.panelAccent,
      color: "#fff",
      boxShadow: ACTIVE_SHADOW,
    },
  }),
};

export interface DarkCtaButtonProps extends ButtonProps {
  tone?: DarkCtaTone;
}

export default function DarkCtaButton({
  tone = "solidMain",
  sx,
  children,
  ...props
}: DarkCtaButtonProps) {
  return (
    <Button
      variant="contained"
      {...props}
      sx={[
        (theme) => ({
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
          textTransform: "uppercase",
          fontWeight: 600,
          boxShadow: 2,
          ...toneStyles[tone](theme),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Button>
  );
}
